/**
 * 🥇 AmbergrisPanel — persistent view of banked BG cargo, chests, and combine.
 *
 * Shows outside a Battlegrounds match on the Profile page. Data sources:
 *   GET  /api/battlegrounds/ambergris/state → { carts, carts_count,
 *          ambergris_carried, amber_carryover, chests_unopened,
 *          combinable_chests, chest_cost }
 *   POST /api/battlegrounds/ambergris/combine → mints chests from banked
 *          carryover (60 A each). Carts still require a Base Sentinel deposit.
 *   POST /api/battlegrounds/chest/open (existing) → open one chest
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AmbergrisPanel() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drop, setDrop] = useState(null);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/battlegrounds/ambergris/state`);
      setState(r.data);
    } catch (_e) { /* silent */ }
  };

  useEffect(() => { refresh(); }, []);

  const combine = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/battlegrounds/ambergris/combine`);
      toast.success(`🎁 Combined ${r.data.ambergris_used}A → ${r.data.chests_earned} chest${r.data.chests_earned === 1 ? "" : "s"}!`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't combine.");
    }
    setBusy(false);
  };

  const openChest = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/battlegrounds/chest/open`);
      setDrop(r.data);
      toast.success(`🎁 Opened a chest!`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No chests to open.");
    }
    setBusy(false);
  };

  if (!state) return null;

  const {
    carts_count = 0, ambergris_carried = 0,
    amber_carryover = 0, chests_unopened = 0,
    chest_cost = 60,
    combinable_chests = 0,
  } = state;
  const progress = Math.min(100, Math.round((amber_carryover / chest_cost) * 100));
  const canCombine = amber_carryover >= chest_cost;

  return (
    <div
      className="mt-6 rounded-2xl border border-amber-500/30 bg-gradient-to-br from-[#1a1305] via-[#0A0A0A] to-[#0a0a05] p-5 shadow-[0_0_30px_rgba(245,158,11,0.15)]"
      data-testid="ambergris-panel"
    >
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <div
            className="text-2xl font-black tracking-wide text-amber-300 flex items-center gap-2"
            style={{ fontFamily: "Anton, sans-serif" }}
          >
            <span className="text-3xl">🥇</span> AMBERGRIS
          </div>
          <div className="text-[11px] text-neutral-400 mt-1 max-w-xl leading-relaxed">
            Battleground cargo currency. Bank {chest_cost} at a Base Sentinel to earn a chest — or combine any banked leftover here.
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Pill label="🎁 Chests" value={chests_unopened} testid="amber-chests" />
          <Pill label="🚚 Carts" value={carts_count} testid="amber-carts" />
        </div>
      </div>

      {/* Number cards (Feb 2026 — "All-Time Deposited" tile removed per owner directive 2026-09-30) */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4">
        <StatCard label="In Carts" value={`${ambergris_carried}A`} icon="🚚" testid="amber-in-carts" />
        <StatCard label="Banked (Carryover)" value={`${amber_carryover}A`} icon="🏦" testid="amber-carryover" />
        <StatCard label="Combinable Now" value={`${combinable_chests}🎁`} icon="🔮" testid="amber-combinable" />
      </div>

      {/* Progress toward next chest */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
          <span>Next chest progress</span>
          <span data-testid="amber-progress-text">{amber_carryover} / {chest_cost}</span>
        </div>
        <div className="h-2 rounded-full bg-neutral-900 border border-neutral-700 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-300 transition-all"
            style={{ width: `${progress}%` }}
            data-testid="amber-progress-bar"
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={combine}
          disabled={!canCombine || busy}
          className={
            "px-4 py-2 rounded-lg text-sm font-bold transition-all " +
            (canCombine
              ? "bg-amber-500 hover:bg-amber-400 text-black shadow-[0_0_20px_rgba(245,158,11,0.5)]"
              : "bg-neutral-800 text-neutral-500 cursor-not-allowed border border-neutral-700")
          }
          data-testid="amber-combine-btn"
          title={canCombine ? `Combine ${chest_cost * combinable_chests}A into ${combinable_chests} chest(s)` : `Need ${chest_cost - amber_carryover} more Ambergris`}
        >
          🔮 Combine {chest_cost}A → 🎁 Chest
        </button>
        <button
          onClick={openChest}
          disabled={chests_unopened <= 0 || busy}
          className={
            "px-4 py-2 rounded-lg text-sm font-bold transition-all " +
            (chests_unopened > 0
              ? "bg-emerald-500/80 hover:bg-emerald-400 text-black"
              : "bg-neutral-800 text-neutral-500 cursor-not-allowed border border-neutral-700")
          }
          data-testid="amber-open-chest-btn"
        >
          🎁 Open Chest ({chests_unopened})
        </button>
      </div>

      {/* Drop toast (inline card) */}
      {drop && drop.drop && (
        <div
          className="mt-4 rounded-lg border border-emerald-400/40 bg-emerald-500/10 p-3 text-emerald-100 text-sm"
          data-testid="amber-drop-result"
        >
          <b>Chest opened!</b>{" "}
          {drop.drop.name && <span>Received: <b>{drop.drop.name}</b>. </span>}
          {drop.honor_awarded && <span>+{drop.honor_awarded} Honor.</span>}
        </div>
      )}

      {/* Hint if carts_count > 0 */}
      {carts_count > 0 && (
        <div className="mt-3 text-[11px] text-amber-300/80">
          💡 You have <b>{carts_count} carts / {ambergris_carried}A</b> in escort. Deposit at your Base Sentinel during a match to convert them.
        </div>
      )}
    </div>
  );
}

function Pill({ label, value, testid }) {
  return (
    <div
      className="px-3 py-1.5 rounded-full text-xs bg-amber-500/15 border border-amber-400/40 text-amber-200 font-semibold"
      data-testid={testid}
    >
      {label}: {value}
    </div>
  );
}

function StatCard({ label, value, icon, testid }) {
  return (
    <div
      className="rounded-lg border border-amber-500/25 bg-neutral-900/60 p-2.5 text-center"
      data-testid={testid}
    >
      <div className="text-2xl mb-1">{icon}</div>
      <div className="text-lg font-bold text-amber-200">{value}</div>
      <div className="text-[9px] uppercase tracking-widest text-neutral-500 mt-0.5">{label}</div>
    </div>
  );
}
